// Parts Finder tour - /parts-finder (landing + results views of the SPA).
import { path, visible, exists, loggedIn, isDesktop } from './util.js';

var YEAR_FIRST = /^(ford|toyota)$/i;

function landing() { return exists('#root [data-tour="pf-vehicle-panel"]'); }
function results() { return exists('#root .pc-grid article.pc-card:not([data-skeleton])'); }
function firstCard() { return visible('#root .pc-grid article.pc-card:not([data-skeleton])'); }
function inFirstCard(sel) { return function () { var c = firstCard(); return c ? c.querySelector(sel) : null; }; }
function makeValue() { var m = document.querySelector('#root [data-tour="pf-make"]'); return m ? m.getAttribute('data-value') || '' : ''; }

export default {
  id: 'parts-finder',
  version: 1,
  title: 'Find parts that fit',
  icon: 'search',
  duration: '45 sec',
  available: function () {
    var p = path();
    return (p === '/parts-finder' || p.indexOf('/parts-finder/') === 0) && p.indexOf('/my-garage') === -1 && p.indexOf('/admin') === -1;
  },
  readySelector: '#root [data-tour="pf-vehicle-panel"], #root .pc-grid article.pc-card:not([data-skeleton])',
  invite: {
    kicker: 'Parts Finder',
    title: 'Find the right part, first time',
    body: 'A quick walk-through of searching by part or by vehicle, so you only see parts that fit.',
  },
  steps: [
    {
      title: 'Find the right part, first time',
      body: 'Search by part name or number, or tell us your vehicle and we will only show parts that fit. Here is how it works.',
      icon: 'compass',
      hero: 'intro',
    },

    // ── Landing view ─────────────────────────────────────────────────────
    {
      when: landing,
      target: '#root [data-tour="pf-search"]',
      icon: 'search',
      title: 'Search by part or part number',
      body: 'Type a part name, an OEM number or a Goldwagen code. Suggestions appear as you type.',
      interactive: {
        on: 'input',
        hint: 'Try it: type “brake pads”',
        done: 'That’s it. Pick a suggestion or press Enter to search.',
        autoAdvance: false,
      },
      includeDescendants: true,
    },
    {
      when: landing,
      target: '#root [data-tour="pf-vehicle-panel"]',
      icon: 'car',
      title: 'Or shop by vehicle',
      body: 'Choose your vehicle once and every result is matched to it, so there is no guessing about fitment.',
      placement: 'right',
    },
    {
      when: landing,
      target: '#root [data-tour="pf-make"]',
      icon: 'car',
      title: 'Start with the make',
      body: 'Open the list and pick your make. You can type to filter it, so “toy” finds Toyota.',
      includeDescendants: true,
      padding: 6,
      interactive: {
        check: function () { return !!makeValue(); },
        hint: 'Your turn: pick a make',
        done: 'Nice. The remaining lists now only show options for that make.',
        delay: 1300,
      },
    },
    {
      when: landing,
      target: '#root [data-tour="pf-vehicle-panel"] .grid',
      icon: 'list',
      title: function () { return YEAR_FIRST.test(makeValue()) ? 'Year, then model and variant' : 'Model, variant and year'; },
      body: function () {
        return YEAR_FIRST.test(makeValue())
          ? makeValue() + ' ranges change a lot between years, so pick the <b>year first</b>. The model and variant lists then narrow to match.'
          : 'Each list narrows to fit what you picked before it. Make and model are enough to search, and adding variant and year gives the tightest match.';
      },
      padding: 10,
    },
    {
      when: landing,
      target: '#root [data-tour="pf-find-parts"]',
      icon: 'search',
      title: 'See every part that fits',
      body: 'Once a make or model is chosen, <b>Find Parts</b> shows the full catalogue for your vehicle, sorted with in-stock parts first.',
    },
    {
      when: landing,
      target: '#root [data-tour="pf-add-garage"]',
      icon: 'garage',
      title: 'Save it to My Garage',
      body: function () {
        return loggedIn()
          ? 'With all four fields filled in, save the vehicle to <b>My Garage</b>. Next time it is one tap away, on any device.'
          : 'Sign in and save the vehicle to <b>My Garage</b>. Next time it is one tap away, on any device.';
      },
    },
    {
      when: landing,
      target: '#root [data-tour="pf-departments"]',
      icon: 'grid',
      title: 'Not sure what it’s called?',
      body: 'Browse by department, from Brakes to Filters to Suspension, and narrow it down from there.',
      optional: true,
      waitFor: 1500,
    },

    // ── Results view ─────────────────────────────────────────────────────
    {
      when: function () { return results() && isDesktop(); },
      target: '#root [data-tour="pf-sidebar-vehicle"]',
      icon: 'car',
      title: 'Your vehicle, always in view',
      body: 'Change make, model, variant or year here and the results update straight away.',
      optional: true,
      waitFor: 1500,
    },
    {
      when: function () { return results() && isDesktop(); },
      target: '#root [data-tour="pf-facets"]',
      icon: 'filter',
      title: 'Narrow it down',
      body: 'Filter by brand and category. The counts show how many matching parts are in each.',
      optional: true,
      waitFor: 1500,
      placement: 'right',
    },
    {
      when: function () { return results() && !isDesktop(); },
      target: '#root [data-tour="pf-filters-btn"]',
      icon: 'filter',
      title: 'Vehicle & filters',
      body: 'Tap here to change your vehicle or to filter by brand and category.',
      optional: true,
      waitFor: 1500,
    },
    {
      when: function () { return results() && exists('#root [data-tour="pf-applied-filters"]'); },
      target: '#root [data-tour="pf-applied-filters"]',
      icon: 'filter',
      title: 'Active filters',
      body: 'Everything you have applied shows here. Tap a chip to remove it, or clear them all.',
      optional: true,
      waitFor: 800,
    },
    {
      when: results,
      target: '#root #sort',
      icon: 'sort',
      title: 'Sort your way',
      body: 'Sort by price or brand. The default puts in-stock parts first, then best match when you search or A to Z when you browse.',
      optional: true,
      waitFor: 1200,
    },
    {
      when: results,
      target: firstCard,
      icon: 'grid',
      title: 'Everything at a glance',
      body: 'Each card shows the brand, part number, price and stock at your selected store.',
      padding: 6,
    },
    {
      when: function () { return results() && !!inFirstCard('.pc-btn-quickview')(); },
      target: inFirstCard('.pc-btn-quickview'),
      icon: 'eye',
      title: 'Quick View',
      body: 'Specs, compatible vehicles and alternatives in a pop-up, without leaving your results.',
      optional: true,
    },
    {
      when: function () { return results() && loggedIn() && !!inFirstCard('.pc-btn-cart')(); },
      target: inFirstCard('.pc-btn-cart'),
      icon: 'cart',
      title: 'Straight to cart',
      body: 'Add the part to your cart from here. Stock is checked against your store as you go.',
      optional: true,
    },

    // ── Global header shortcuts ──────────────────────────────────────────
    {
      target: '#fulfilment-selector-btn, #branch-selector-btn',
      icon: 'store',
      title: 'Prices & stock are per store',
      body: 'Choose your nearest Goldwagen store so stock, collection and delivery options are accurate.',
      optional: true,
      waitFor: 800,
    },
    {
      title: 'You’re all set',
      body: 'Reopen this tour any time from the <b>Tours</b> button in the corner. Happy hunting!',
      hero: 'success',
      nextLabel: 'Start searching',
    },
  ],
};
